import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Leaf, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { fetchProduct, fetchProducts, DISCLAIMER } from "../lib/api";

const LOOSE_OPTIONS = [
  { size: "Small bag (~6–8 cups)", price: 8 },
  { size: "Medium jar", price: 12 },
  { size: "2 small bags", price: 15 },
  { size: "2 medium jars", price: 22 },
  { size: "XL jar (~28–30 cups)", price: 25 },
  { size: "10 tea bags", price: 2 },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [option, setOption] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    fetchProduct(slug).then((p) => {
      setProduct(p);
      setOption(null);
      if (p.type === "tea") fetchProducts({ collection: p.collection }).then((all) => setRelated(all.filter((x) => x.slug !== p.slug).slice(0, 3)));
      else setRelated([]);
    });
  }, [slug]);

  if (!product) return <div className="pt-40 text-center text-forest-soft">Steeping…</div>;

  const isTea = product.type === "tea";
  const isSalve = product.type === "salve";
  const isWomensHealth = product.collection === "womens-health";
  const options = (isTea && !isWomensHealth) ? LOOSE_OPTIONS : isSalve ? [{ size: "2oz", price: 12 }, { size: "4oz", price: 24 }] : null;

  const add = () => {
    if (options && !option) {
      toast.error("Please choose a size first.");
      return;
    }
    addItem(product, option || {});
    toast.success(`${product.name} added to your basket`);
  };

  return (
    <div className="pt-28 md:pt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-forest-soft hover:text-terracotta mb-10" style={{ transition: "color 0.3s ease" }}>
          <ArrowLeft className="w-4 h-4" /> Back to shop
        </Link>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <div className="rounded-t-[8rem] rounded-b-3xl overflow-hidden bg-surface aspect-[4/5] sticky top-28">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
            </div>
          </motion.div>

          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-4">{product.type === "bundle" ? "Bundle" : product.type === "other" ? "Wellness" : product.collection?.replace(/-/g, " ")}</p>
              <h1 className="font-serif text-4xl md:text-6xl text-forest tracking-tight leading-none mb-5" data-testid="product-name">{product.name}</h1>
              <p className="text-forest-soft text-lg leading-relaxed mb-8">{product.benefits}</p>

              {product.vibe && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {product.vibe.map((v) => (
                    <span key={v} className="text-xs uppercase tracking-[0.12em] text-forest bg-surface rounded-full px-4 py-1.5">{v}</span>
                  ))}
                </div>
              )}

              {product.ingredients && (
                <div className="border-t border-forest/10 py-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-2 flex items-center gap-2"><Leaf className="w-3.5 h-3.5" /> Ingredients</p>
                  <p className="text-forest">{product.ingredients}</p>
                </div>
              )}
              {product.best_time && (
                <div className="border-t border-forest/10 py-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-2 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Best time to drink</p>
                  <p className="text-forest">{product.best_time}</p>
                </div>
              )}
              {product.contents && (
                <div className="border-t border-forest/10 py-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-3">What&rsquo;s inside</p>
                  <ul className="space-y-2">
                    {product.contents.map((c) => (
                      <li key={c} className="flex items-center gap-2 text-forest"><Check className="w-4 h-4 text-sage" /> {c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {options && (
                <div className="border-t border-forest/10 py-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-3">Choose a size</p>
                  <div className="flex flex-wrap gap-3">
                    {options.map((o) => (
                      <button
                        key={o.size}
                        onClick={() => setOption(o)}
                        data-testid={`option-${o.price}`}
                        className={`rounded-full border px-5 py-3 text-sm ${option?.size === o.size ? "bg-forest text-cream border-forest" : "border-forest/20 text-forest hover:border-forest/50"}`}
                        style={{ transition: "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease" }}
                      >
                        {o.size} — ${o.price}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-6 mt-8">
                <span className="font-serif text-4xl text-forest" data-testid="product-price">
                  ${option ? option.price : product.price}
                </span>
                <button
                  onClick={add}
                  data-testid="detail-add-to-cart"
                  className="flex-1 rounded-full bg-forest hover:bg-terracotta text-cream py-4 text-sm uppercase tracking-widest font-semibold"
                  style={{ transition: "background-color 0.3s ease" }}
                >
                  Add to Basket
                </button>
              </div>

              {product.extra_note && (
                <div className="mt-8 rounded-2xl bg-dusty-rose/25 border border-dusty-rose/40 p-5" data-testid="product-extra-note">
                  <p className="text-sm text-forest leading-relaxed">{product.extra_note}</p>
                </div>
              )}
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-32">
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-10">You may also love</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {related.map((r, i) => <ProductCard key={r.slug} product={r} index={i} />)}
            </div>
          </div>
        )}

        <div className="mt-24 rounded-2xl bg-surface p-6">
          <p className="text-xs leading-relaxed text-forest-soft">{DISCLAIMER}</p>
        </div>
      </div>
    </div>
  );
}
